import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Button from '@/components/base/Button';
import {
  Field,
  SelectField,
  TextareaField,
  CheckboxField,
  Honeypot,
  FormStatus,
} from '@/components/base/FormFields';
import {
  CategorySelect,
  CategoryCheckboxGroup,
  EventTypeSelect,
} from '@/components/feature/CategoryFields';
import { useFormSubmit } from '@/hooks/useFormSubmit';
import { CONTACT_METHODS } from '@/data/site';

type EnquiryFormProps = {
  submitAddr: string;
  formId: string;
  categoryMode: 'single' | 'multiple';
  submitLabel: string;
  successMessage: string;
  priceNote?: string;
  children?: ReactNode;
  requirementsMaxLength?: number;
  eventDateMode?: 'date' | 'text';
  supplierBudgetLabel?: string;
  supplierBudgetHint?: string;
};

export default function EnquiryForm({
  submitAddr,
  formId,
  categoryMode,
  submitLabel,
  successMessage,
  priceNote,
  children,
  requirementsMaxLength = 500,
  eventDateMode = 'date',
  supplierBudgetLabel = 'Supplier budget',
  supplierBudgetHint = 'An indication of budget helps us match you well.',
}: EnquiryFormProps) {
  const { status, submitting, errorMessage, handleSubmit } = useFormSubmit();
  const [category, setCategory] = useState('');
  const [otherCategory, setOtherCategory] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  const [eventType, setEventType] = useState('');
  const [eventTypeOther, setEventTypeOther] = useState('');
  const [localError, setLocalError] = useState('');

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (submitting) {
      event.preventDefault();
      return;
    }
    if (categoryMode === 'multiple' && categories.length === 0) {
      event.preventDefault();
      setLocalError('Please select at least one supplier category.');
      return;
    }
    setLocalError('');
    void handleSubmit(event, submitAddr);
  };

  return (
    <form
      id={formId}
      name={formId}
      data-netlify="true"
      data-readdy-form={formId}
      onSubmit={onSubmit}
      className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-8 space-y-6"
      method="post"
    >
      {/* Required for Netlify Forms detection and routing. */}
      <input type="hidden" name="form-name" value={formId} />

      {priceNote ? (
        <p className="rounded-md bg-[#E6E8EC] border-l-4 border-[#D6A441] px-4 py-3 text-[15px] leading-relaxed text-[#2F343A]">
          {priceNote}
        </p>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field
          label="First name"
          name="first_name"
          required
          autoComplete="given-name"
          placeholder="Your first name"
        />
        <Field
          label="Last name"
          name="last_name"
          required
          autoComplete="family-name"
          placeholder="Your last name"
        />
        <Field
          label="Business name (optional)"
          name="business_name"
          autoComplete="organization"
          placeholder="If enquiring on behalf of a business"
        />
        <Field
          label="Email"
          name="email"
          type="email"
          required
          autoComplete="email"
          placeholder="you@example.com"
        />
        <Field
          label="Telephone"
          name="telephone"
          type="tel"
          required
          autoComplete="tel"
          placeholder="Your contact number"
          hint="We may give you a quick call to clarify your requirements and help us find the right connection."
        />
        <SelectField
          label="Preferred contact method"
          name="preferred_contact_method"
          options={CONTACT_METHODS}
          defaultValue="Either"
          required
        />
      </div>

      <EventTypeSelect
        value={eventType}
        onChange={setEventType}
        otherValue={eventTypeOther}
        onOtherChange={setEventTypeOther}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {eventDateMode === 'text' ? (
          <Field
            label="Event date (approximate is fine)"
            name="event_date"
            required
            placeholder="e.g. September 2026, or flexible"
            hint="An approximate date or month is absolutely fine."
          />
        ) : (
          <Field label="Event date" name="event_date" type="date" required />
        )}
        <Field
          label="Event location"
          name="event_location"
          required
          placeholder="Venue or area, e.g. Shoreditch, London"
        />
      </div>

      <Field
        label={supplierBudgetLabel}
        name="supplier_budget"
        required
        placeholder="e.g. £500–£1,500"
        hint={supplierBudgetHint}
      />

      {categoryMode === 'single' ? (
        <CategorySelect
          value={category}
          onChange={setCategory}
          otherValue={otherCategory}
          onOtherChange={setOtherCategory}
        />
      ) : (
        <CategoryCheckboxGroup
          values={categories}
          onChange={setCategories}
          otherValue={otherCategory}
          onOtherChange={setOtherCategory}
          error={localError}
        />
      )}

      <TextareaField
        label="Your requirements"
        name="requirements"
        required
        rows={6}
        maxLength={requirementsMaxLength}
        placeholder="Tell us what you're looking for, your vision, style preferences and anything else that matters."
      />

      {children}

      <TextareaField
        label="Additional information (optional)"
        name="additional_info"
        rows={3}
        placeholder="Anything else you'd like us to know."
      />

      <div className="space-y-4 pt-2">
        <CheckboxField
          name="privacy_ack"
          required
          label={
            <>
              I have read and understand the{' '}
              <Link to="/privacy-policy" className="text-primary-300 underline underline-offset-2">
                Privacy Policy
              </Link>{' '}
              and agree to KOR38 using my details to respond to this enquiry.{' '}
              <span className="text-primary-400">*</span>
            </>
          }
        />
        <CheckboxField
          name="marketing_consent"
          label="I would like to receive occasional news and offers from KOR38. (Optional)"
        />
      </div>

      <Honeypot />

      <div className="space-y-4">
        {localError ? (
          <p role="alert" className="text-[15px] text-red-300">
            {localError}
          </p>
        ) : null}
        <FormStatus status={status} successMessage={successMessage} errorMessage={errorMessage} />
        <Button
          type="submit"
          variant="primary"
          size="lg"
          disabled={submitting}
          className="w-full sm:w-auto"
        >
          {submitting ? 'Sending…' : submitLabel}
        </Button>
      </div>
    </form>
  );
}